import { type MouseEvent, type ReactNode, useState, Children, } from "react";
import styled from "styled-components";



export declare type OnExpandListener = (evt: MouseEvent<HTMLButtonElement>, { index }: { index: number }) => void;

export declare type AccordionProps = {
	children: ReactNode;
	titles: string[] | string;
	multi?: boolean;
	onExpand?: OnExpandListener;
}



export const Accordion = ({ children, titles, multi = false, onExpand, }: AccordionProps) => {
	const [openIndices, setOpenIndices] = useState<number[]>([]);

	// Convert titles to array if it's a comma-separated string
	const titleArray = typeof titles === "string" ? titles.split(",").map(t => t.trim()) : titles;

	// Convert children to array
	const childrenArray = Children.toArray(children);

	const toggleAccordion = (evt: MouseEvent<HTMLButtonElement>, index: number) => {
		const wasOpen = openIndices.includes(index);
		
		if (multi) {
			// Allow multiple items to be open
			setOpenIndices(prev => 
				prev.includes(index) 
					? prev.filter(i => i !== index) 
					: [...prev, index]
			);
		} else {
			// Only allow one item to be open at a time
			setOpenIndices(prev => 
				prev.includes(index) ? [] : [index]
			);
		}

		// Call onExpand when opening an item (not when closing)
		if (!wasOpen && onExpand) {
			onExpand(evt, { index });
		}
	};

	return (
		<AccordionContainer>
			{childrenArray.map((child, index) => {
				const isOpen = openIndices.includes(index);
				const title = titleArray[index] || `Accordion title ${index + 1}`;

				return (
					<AccordionItem key={index}>
						<AccordionHeader onClick={(evt) => toggleAccordion(evt, index)} $isOpen={isOpen}>
							<AccordionTitle>{title}</AccordionTitle>
							<AccordionIcon $isOpen={isOpen}>
								{isOpen ? "−" : "+"}
							</AccordionIcon>
						</AccordionHeader>
						<AccordionContent $isOpen={isOpen}>
							<AccordionContentInner $isOpen={isOpen}>
								{child}
							</AccordionContentInner>
						</AccordionContent>
					</AccordionItem>
				);
			})}
		</AccordionContainer>
	);
};



const AccordionContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const AccordionItem = styled.div`
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const AccordionHeader = styled.button<{ $isOpen: boolean }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 1.5rem 0;
	background: transparent;
	border: none;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.8;
	}
`;

const AccordionTitle = styled.span`
	font-family: 'Space Grotesk', sans-serif;
	font-size: 16px;
	line-height: 24px;
	color: #AAACA7;
	font-weight: 400;
	text-align: left;
`;

const AccordionIcon = styled.span<{ $isOpen: boolean }>`
	font-family: 'Space Grotesk', sans-serif;
	font-size: 24px;
	color: #AAACA7;
	font-weight: 300;
	transition: transform 0.3s ease;
	user-select: none;
`;

const AccordionContent = styled.div<{ $isOpen: boolean }>`
	max-height: ${({ $isOpen }) => ($isOpen ? "50000px" : "0")};
	overflow: hidden;
	transition: max-height 0.3s ease;
`;

const AccordionContentInner = styled.div<{ $isOpen: boolean }>`
	padding: ${({ $isOpen }) => ($isOpen ? "0 0 1.5rem 0" : "0")};
	opacity: ${({ $isOpen }) => ($isOpen ? "1" : "0")};
	transition: opacity 0.3s ease, padding 0.3s ease;
`;

